import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); }
    }
  `,
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%)',
    padding: '20px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(61, 79, 79, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    animation: 'fadeIn 0.5s ease-out',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#6b8080',
    fontSize: '15px',
  },
  tabs: {
    display: 'flex',
    marginBottom: '24px',
    background: 'rgba(124, 152, 133, 0.1)',
    borderRadius: '12px',
    padding: '4px',
  },
  tab: {
    flex: 1,
    padding: '12px',
    border: 'none',
    background: 'transparent',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    color: '#6b8080',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  tabActive: {
    background: 'white',
    color: '#3d4f4f',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    position: 'relative',
  },
  input: {
    width: '100%',
    padding: '16px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    background: 'white',
  },
  inputFocused: {
    borderColor: '#7c9885',
    boxShadow: '0 0 0 4px rgba(124, 152, 133, 0.1)',
  },
  inputRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  roleSelector: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  roleButton: {
    padding: '16px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '12px',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  roleButtonActive: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.1)',
  },
  roleEmoji: {
    fontSize: '24px',
    marginBottom: '8px',
    display: 'block',
  },
  roleText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#3d4f4f',
  },
  button: {
    padding: '16px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginBottom: '16px',
    fontSize: '14px',
    textAlign: 'center',
  },
  messageError: {
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#dc2626',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  messageSuccess: {
    background: 'rgba(34, 197, 94, 0.1)',
    color: '#16a34a',
    border: '1px solid rgba(34, 197, 94, 0.2)',
  },
  verificationBox: {
    textAlign: 'center',
    padding: '24px 0',
  },
  verificationIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  verificationTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '12px',
  },
  verificationText: {
    color: '#6b8080',
    fontSize: '15px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  verificationEmail: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '16px',
  },
  resendButton: {
    padding: '12px 24px',
    background: 'rgba(124, 152, 133, 0.1)',
    border: 'none',
    borderRadius: '10px',
    color: '#5a7a65',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    marginTop: '8px',
  },
  backButton: {
    width: '100%',
    padding: '12px',
    background: 'transparent',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    color: '#5a7070',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    marginTop: '16px',
  },
  // Consent styles
  consentSection: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    accentColor: '#7c9885',
    cursor: 'pointer',
    flexShrink: 0,
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#5a7070',
    lineHeight: '1.5',
  },
  tosLink: {
    color: '#5a7a65',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: '500',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    padding: '24px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(124, 152, 133, 0.1)',
    cursor: 'pointer',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#5a7070',
    transition: 'all 0.2s ease',
  },
  modalBody: {
    padding: '24px',
    maxHeight: 'calc(80vh - 140px)',
    overflowY: 'auto',
  },
  modalFooter: {
    padding: '20px 24px',
    borderTop: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  tosContent: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: '#4a5568',
  },
  tosSection: {
    marginBottom: '20px',
  },
  tosSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },
};

// Terms of Service content
const TermsOfServiceModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Terms of Service</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.tosContent}>
            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>1. Introduction</h3>
              <p>
                Welcome to Instavju. This website is a private platform owned and operated by Anton Ræder. 
                By accessing or using our service, you agree to be bound by these Terms of Service.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>2. Acceptance of Terms</h3>
              <p>
                By creating an account and using Instavju, you confirm that you have read, understood, 
                and agree to be bound by these Terms of Service. If you do not agree to these terms, 
                you may not use the service.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>3. User Accounts</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials 
                and for all activities that occur under your account. You agree to provide accurate 
                and complete information when creating your account and to update such information 
                as necessary.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>4. Acceptable Use</h3>
              <p>
                You agree to use Instavju only for lawful purposes and in accordance with these Terms. 
                You agree not to use the service in any way that could damage, disable, or impair the 
                service, or interfere with any other party's use of the service.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>5. Privacy</h3>
              <p>
                Your use of Instavju is also governed by our Privacy Policy. By using the service, 
                you consent to the collection and use of information as described in our Privacy Policy.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>6. Intellectual Property</h3>
              <p>
                The service and its original content, features, and functionality are owned by 
                Anton Ræder and are protected by international copyright, trademark, and other 
                intellectual property laws.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>7. User Content</h3>
              <p>
                You retain ownership of any content you submit to the service, including CVs, 
                job listings, and profile information. By submitting content, you grant us a 
                license to use, store, and display such content as necessary to provide the service.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>8. Disclaimer of Warranties</h3>
              <p>
                The service is provided "as is" without warranties of any kind, either express or 
                implied. We do not guarantee that the service will be uninterrupted, secure, or 
                error-free.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>9. Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by law, Anton Ræder shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages resulting from 
                your use of or inability to use the service.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>10. Termination</h3>
              <p>
                We reserve the right to terminate or suspend your account and access to the service 
                at our sole discretion, without notice, for conduct that we believe violates these 
                Terms or is harmful to other users, us, or third parties.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>11. Changes to Terms</h3>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of any 
                material changes by posting the new Terms on the service. Your continued use of the 
                service after such changes constitutes your acceptance of the new Terms.
              </p>
            </div>

            <div style={styles.tosSection}>
              <h3 style={styles.tosSectionTitle}>12. Contact Information</h3>
              <p>
                If you have any questions about these Terms, please contact us through the platform.
              </p>
            </div>

            <div style={{ ...styles.tosSection, marginBottom: 0 }}>
              <p style={{ fontStyle: 'italic', color: '#6b8080' }}>
                Last updated: February 2026
              </p>
            </div>
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Component receives setUser from App.js
function AuthPage({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState('');
  
  // Email verification states
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Consent states
  const [acceptTOS, setAcceptTOS] = useState(false);
  const [acceptPromotionalUse, setAcceptPromotionalUse] = useState(false);
  const [showTOSModal, setShowTOSModal] = useState(false);

  // Check URL params for mode
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [searchParams]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setRole('');
    setAcceptTOS(false);
    setAcceptPromotionalUse(false);
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendingEmail) return;
    
    setResendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail }),
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ text: 'Verification email sent!', type: 'success' });
        setResendCooldown(60);
      } else {
        setMessage({ text: data.message || 'Failed to resend email', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Failed to resend email', type: 'error' });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerificationMessage(false);
    setIsLogin(true);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await response.json();

        if (data.success) {
          localStorage.setItem('token', data.token);
          setUser(data.data.user);
          
          if (!data.data.user.signUpFinished) {
            if (data.data.user.role === 'job_seeker') {
              navigate('/createcv');
            } else {
              navigate('/createjoblisting');
            }
          } else {
            navigate('/home');
          }
        } else {
          // Check if email verification is required
          if (data.requiresVerification) {
            setVerificationEmail(email);
            setShowVerificationMessage(true);
          } else {
            setMessage({ text: data.message || 'Login failed', type: 'error' });
          }
        }
      } else {
        // Registration validation
        if (password !== confirmPassword) {
          setMessage({ text: 'Passwords do not match', type: 'error' });
          setLoading(false);
          return;
        }

        if (!role) {
          setMessage({ text: 'Please select a role', type: 'error' });
          setLoading(false);
          return;
        }

        if (!acceptTOS) {
          setMessage({ text: 'You must accept the Terms of Service', type: 'error' });
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email, 
            password, 
            firstName, 
            lastName, 
            role,
            acceptedTOS: acceptTOS,
            acceptedPromotionalUse: acceptPromotionalUse,
          }),
        });
        const data = await response.json();

        if (data.success) {
          // Show verification message
          setVerificationEmail(email);
          setShowVerificationMessage(true);
          resetForm();
        } else {
          setMessage({ text: data.message || 'Registration failed', type: 'error' });
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setMessage({ text: 'An error occurred. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles.global}</style>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <h1 style={styles.logoText}>Instavju</h1>
            <p style={styles.subtitle}>A job interview in under 5 minutes</p>
          </div>

          {message.text && (
            <div style={{
              ...styles.message,
              ...(message.type === 'error' ? styles.messageError : styles.messageSuccess)
            }}>
              {message.text}
            </div>
          )}

          {/* Verification Message Screen */}
          {showVerificationMessage ? (
            <>
              <div style={styles.verificationBox}>
                <div style={styles.verificationIcon}>📧</div>
                <h2 style={styles.verificationTitle}>Check your email</h2>
                <p style={styles.verificationText}>
                  We've sent a verification link to:
                </p>
                <p style={styles.verificationEmail}>{verificationEmail}</p>
                <p style={styles.verificationText}>
                  Click the link in the email to verify your account and get started.
                </p>
                <button
                  style={{
                    ...styles.resendButton,
                    opacity: resendCooldown > 0 || resendingEmail ? 0.6 : 1,
                    cursor: resendCooldown > 0 || resendingEmail ? 'not-allowed' : 'pointer',
                  }}
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || resendingEmail}
                >
                  {resendingEmail 
                    ? 'Sending...' 
                    : resendCooldown > 0 
                      ? `Resend in ${resendCooldown}s` 
                      : 'Resend verification email'}
                </button>
              </div>
              <button
                style={styles.backButton}
                onClick={handleBackToLogin}
              >
                ← Back to sign in
              </button>
            </>
          ) : (
            <>
              <div style={styles.tabs}>
                <button
                  style={{ ...styles.tab, ...(isLogin ? styles.tabActive : {}) }}
                  onClick={() => {
                    setIsLogin(true);
                    setMessage({ text: '', type: '' });
                  }}
                >
                  Sign In
                </button>
                <button
                  style={{ ...styles.tab, ...(!isLogin ? styles.tabActive : {}) }}
                  onClick={() => {
                    setIsLogin(false);
                    setMessage({ text: '', type: '' });
                  }}
                >
                  Sign Up
                </button>
              </div>

              <form style={styles.form} onSubmit={handleSubmit}>
                {!isLogin && (
                  <>
                    <div style={styles.inputRow}>
                      <div style={styles.inputGroup}>
                        <input
                          type="text"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          onFocus={() => setFocusedInput('firstName')}
                          onBlur={() => setFocusedInput('')}
                          style={{
                            ...styles.input,
                            ...(focusedInput === 'firstName' ? styles.inputFocused : {}),
                          }}
                          required
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <input
                          type="text"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          onFocus={() => setFocusedInput('lastName')}
                          onBlur={() => setFocusedInput('')}
                          style={{
                            ...styles.input,
                            ...(focusedInput === 'lastName' ? styles.inputFocused : {}),
                          }}
                          required
                        />
                      </div>
                    </div>

                    <div style={styles.roleSelector}>
                      <button
                        type="button"
                        style={{
                          ...styles.roleButton,
                          ...(role === 'job_seeker' ? styles.roleButtonActive : {}),
                        }}
                        onClick={() => setRole('job_seeker')}
                      >
                        <span style={styles.roleEmoji}>👤</span>
                        <span style={styles.roleText}>Job Seeker</span>
                      </button>
                      <button
                        type="button"
                        style={{
                          ...styles.roleButton,
                          ...(role === 'hiring_manager' ? styles.roleButtonActive : {}),
                        }}
                        onClick={() => setRole('hiring_manager')}
                      >
                        <span style={styles.roleEmoji}>💼</span>
                        <span style={styles.roleText}>Hiring Manager</span>
                      </button>
                    </div>
                  </>
                )}

                <div style={styles.inputGroup}>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedInput('email')}
                    onBlur={() => setFocusedInput('')}
                    style={{
                      ...styles.input,
                      ...(focusedInput === 'email' ? styles.inputFocused : {}),
                    }}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput('')}
                    style={{
                      ...styles.input,
                      ...(focusedInput === 'password' ? styles.inputFocused : {}),
                    }}
                    required
                  />
                </div>

                {!isLogin && (
                  <>
                    <div style={styles.inputGroup}>
                      <input
                        type="password"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onFocus={() => setFocusedInput('confirmPassword')}
                        onBlur={() => setFocusedInput('')}
                        style={{
                          ...styles.input,
                          ...(focusedInput === 'confirmPassword' ? styles.inputFocused : {}),
                        }}
                        required
                      />
                    </div>

                    {/* Consent Checkboxes */}
                    <div style={styles.consentSection}>
                      <div style={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          id="acceptTOS"
                          checked={acceptTOS}
                          onChange={(e) => setAcceptTOS(e.target.checked)}
                          style={styles.checkbox}
                        />
                        <label htmlFor="acceptTOS" style={styles.checkboxLabel}>
                          I agree to the{' '}
                          <span
                            style={styles.tosLink}
                            onClick={(e) => {
                              e.preventDefault();
                              setShowTOSModal(true);
                            }}
                          >
                            Terms of Service
                          </span>
                          {' '}*
                        </label>
                      </div>

                      <div style={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          id="acceptPromotionalUse"
                          checked={acceptPromotionalUse}
                          onChange={(e) => setAcceptPromotionalUse(e.target.checked)}
                          style={styles.checkbox}
                        />
                        <label htmlFor="acceptPromotionalUse" style={styles.checkboxLabel}>
                          I agree to receive promotional emails and updates about new features (optional)
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* TOS Modal */}
      <TermsOfServiceModal 
        isOpen={showTOSModal} 
        onClose={() => setShowTOSModal(false)} 
      />
    </>
  );
}

export default AuthPage;